import React, { useEffect, useState } from "react";
import ReactModal from "react-modal";
import axios from "axios";

ReactModal.setAppElement("#root"); // same as your other modal

const modalStyles = {
  content: {
    maxWidth: "700px",
    width: "90%",
    margin: "auto",
    padding: "30px",
    borderRadius: "12px",
    border: "4px solid #374151",
    boxShadow: "0 12px 24px rgba(0, 0, 0, 0.6)",
    overflow: "auto",
    backgroundColor: "#f7fafc",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    zIndex: 1000,
  },
};

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between text-sm">
    <span className="text-gray-600">{label}</span>
    <span className="font-medium break-all">{value ?? "-"}</span>
  </div>
);

/* ---------- SKU helpers (outside component) ---------- */
const parseSku = (sku) => {
  if (!sku || typeof sku !== "string") return null;
  const s = sku.trim().toUpperCase();
  // Expected: AAA-YY-SS-EE  e.g., GIA-24-SM-LA
  const m = s.match(/^([A-Z]{3})-(\d{2})-([A-Z]{2})-([A-Z]{2})$/);
  if (!m) return null;
  const [, prefix, year, size, entrance] = m;
  return { prefix, year, size, entrance, raw: s };
};

const areSkusCompatible = (oldSku, newSku) => {
  const a = parseSku(oldSku);
  const b = parseSku(newSku);
  if (!a || !b) {
    // Fallback to strict equality if we can't parse one of them
    return (oldSku || "").toUpperCase() === (newSku || "").toUpperCase();
  }

  // Special rule: GIA years 23 ↔ 24 are interchangeable if size & entrance match
  const allowedYears = new Set(["23", "24"]);
  const isGIAInterchange =
    a.prefix === "GIA" &&
    b.prefix === "GIA" &&
    allowedYears.has(a.year) &&
    allowedYears.has(b.year) &&
    a.size === b.size &&
    a.entrance === b.entrance;

  if (isGIAInterchange) return true;

  // Otherwise require exact component match (equivalent to exact SKU)
  return (
    a.prefix === b.prefix &&
    a.year === b.year &&
    a.size === b.size &&
    a.entrance === b.entrance
  );
};
/* ----------------------------------------------------- */

const ChangeSerialModal = ({
  isOpen,
  toggleModalVisibility,
  order, // row.original
  onSuccess, // still supported, but we won't use onError to show popups
  // onError,   <-- intentionally unused to avoid pop-up bubbles
}) => {
  const [newSerial, setNewSerial] = useState("");
  const [checking, setChecking] = useState(false);
  const [valid, setValid] = useState(false);
  const [msg, setMsg] = useState("");
  const [oldInv, setOldInv] = useState(null);
  const [newInv, setNewInv] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNewSerial("");
      setChecking(false);
      setValid(false);
      setMsg("");
      setOldInv(null);
      setNewInv(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  // Prefer specific, nested API messages (e.g., "This serial number is already taken")
  const getErrMsg = (err) => {
    const d = err?.response?.data;

    // Server now forwards Circuly's best message into top-level `message`
    if (d?.message) return d.message;

    // Robust fallbacks for common Circuly shapes
    const candidates = [
      d?.errors?.serial_number?.[0],
      d?.error?.errors?.serial_number?.[0],
      d?.error?.serial_number?.[0],
      d?.error?.error?.serial_number?.[0],
      d?.error?.message,
      typeof d === "string" ? d : null,
      err?.message,
    ].filter(Boolean);

    return candidates[0] || "Unknown error";
  };

  const runChecks = async () => {
    setChecking(true);
    setValid(false);
    setMsg("");
    setOldInv(null);
    setNewInv(null);
    try {
      const [oldRes, newRes] = await Promise.all([
        axios.get(`/api/inventory/${order.serialNumber}`),
        axios.get(`/api/inventory/${newSerial}`),
      ]);

      const _oldInv = oldRes.data;
      const _newInv = newRes.data;
      setOldInv(_oldInv);
      setNewInv(_newInv);

      const newOp = (_newInv?.status?.operationalStatus || "").toLowerCase();
      const skuOld = _oldInv?.model?.SKU;
      const skuNew = _newInv?.model?.SKU;

      if (!newSerial || newSerial === order.serialNumber) {
        setMsg("New serial is empty or the same as current.");
        setValid(false);
      } else if (newOp !== "in stock") {
        setMsg(
          `New bike is not "in stock" (current: ${
            _newInv?.status?.operationalStatus || "unknown"
          })`
        );
        setValid(false);
      } else if (!skuOld || !skuNew || !areSkusCompatible(skuOld, skuNew)) {
        const a = parseSku(skuOld);
        const b = parseSku(skuNew);
        if (a && b && a.prefix === "GIA" && b.prefix === "GIA") {
          setMsg(
            `SKU mismatch. Allowed only for GIA year swap 23↔24 with same size & entrance.
Current: ${skuOld} (${a.prefix}-${a.year}-${a.size}-${a.entrance})
New:     ${skuNew} (${b.prefix}-${b.year}-${b.size}-${b.entrance})`
          );
        } else {
          setMsg(
            `SKU mismatch: current ${skuOld || "-"} vs new ${skuNew || "-"}`
          );
        }
        setValid(false);
      } else {
        setMsg("Checks passed. Ready to swap.");
        setValid(true);
      }
    } catch (e) {
      console.error(e);
      setMsg("Could not load inventory for the provided serial(s).");
      setValid(false);
    } finally {
      setChecking(false);
    }
  };

  const confirmSwap = async () => {
    setSubmitting(true);

    const oldSerial = order.serialNumber;
    const oldStatus = oldInv?.status?.operationalStatus || "in stock";

    // Step A: update Circuly (NO override allowed)
    const setCirculySerial = async (serial) =>
      axios.put(`/api/circuly/${order.subscriptionId}/serial`, {
        serial_number: serial,
      });

    // Step B: inventory updates
    const setOpStatus = (serial, status) =>
      axios.put(`/api/inventory/${serial}/operationalStatus`, {
        operationalStatus: status,
      });

    // Step C: order update
    const setOrderSerial = () =>
      axios.put(`/api/orders/${order._id}`, {
        serialNumber: newSerial, // serial-only swap → do NOT touch subscription_Swapped
      });

    try {
      // A) Circuly → new serial (if this fails, stop and show the error inline)
      await setCirculySerial(newSerial);

      // B) Inventory statuses
      await setOpStatus(oldSerial, "in stock");
      await setOpStatus(newSerial, oldStatus);

      // C) Order in DB
      const orderRes = await setOrderSerial();

      onSuccess?.({
        message: "Serial number swapped successfully",
        order: orderRes.data,
        previousSerial: oldSerial,
        newSerial,
        transferredStatus: oldStatus,
      });
      toggleModalVisibility();
    } catch (err) {
      console.error("Swap failed:", err?.response?.data || err.message);
      // Show the detailed reason inline (same place as "Checks passed")
      setMsg(getErrMsg(err));
      setValid(false);
      // Do NOT call onError — avoids parent alert/pop-up
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={toggleModalVisibility}
      contentLabel="Change Serial Number"
      style={modalStyles}
    >
      <div className="space-y-6">
        <div className="text-center mb-2">
          <h2 className="text-gray-900 text-3xl font-bold">
            Change Serial number
          </h2>
          <p className="text-gray-600 mt-1">
            Swap bikes safely with pre-checks.
          </p>
        </div>

        <hr className="border-t border-gray-300" />

        <div className="bg-white rounded shadow-sm p-4 space-y-2">
          <InfoRow label="Order ID" value={order._id} />
          <InfoRow label="Subscription ID" value={order.subscriptionId} />
          <InfoRow
            label="Customer"
            value={`${order.firstName} ${order.lastName}`}
          />
          <InfoRow label="Current Serial Number" value={order.serialNumber} />
          <InfoRow label="Item (SKU name)" value={order.itemSkuName} />
        </div>

        <div className="bg-white rounded shadow-sm p-4 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              New serial number
            </label>
            <input
              type="text"
              className="border-gray-300 shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-1 focus:ring-joule"
              placeholder="e.g. 1463"
              value={newSerial}
              onChange={(e) => setNewSerial(e.target.value.trim())}
              onKeyDown={(e) => e.key === "Enter" && runChecks()}
            />

            <div className="flex items-center gap-2 mt-3">
              <button
                type="button"
                onClick={runChecks}
                disabled={!newSerial || checking}
                className={`py-2 px-4 font-semibold rounded-lg shadow-md bg-joule text-white hover:bg-jouledark focus:outline-none ${
                  checking ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {checking ? "Checking..." : "Run checks"}
              </button>

              <button
                type="button"
                onClick={confirmSwap}
                disabled={!valid || submitting}
                className={`py-2 px-4 font-semibold rounded-lg shadow-md bg-joule text-white hover:bg-gray-900 focus:outline-none ${
                  !valid || submitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {submitting ? "Swapping..." : "Confirm swap"}
              </button>

              <button
                type="button"
                className="py-2 px-4 font-semibold rounded-lg shadow-md bg-red-500 text-white hover:bg-red-700 focus:outline-none"
                onClick={toggleModalVisibility}
              >
                Cancel
              </button>
            </div>

            {/* Inline status/error message (same place as "Checks passed") */}
            <p
              className={`mt-3 text-sm ${
                valid ? "text-green-700" : "text-red-700"
              }`}
            >
              {msg || "Enter a new serial number and click Run checks."}
            </p>
          </div>

          {(newInv || oldInv) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded p-3">
                <div className="font-semibold mb-2">Current bike</div>
                <InfoRow label="Serial number" value={order.serialNumber} />
                <InfoRow
                  label="Op. status"
                  value={oldInv?.status?.operationalStatus}
                />
                <InfoRow label="SKU" value={oldInv?.model?.SKU} />
                <InfoRow
                  label="Model"
                  value={`${oldInv?.model?.brand ?? ""} ${
                    oldInv?.model?.type ?? ""
                  }`}
                />
              </div>
              <div className="border rounded p-3">
                <div className="font-semibold mb-2">New bike</div>
                <InfoRow label="Serial number" value={newSerial} />
                <InfoRow
                  label="Op. status"
                  value={newInv?.status?.operationalStatus}
                />
                <InfoRow label="SKU" value={newInv?.model?.SKU} />
                <InfoRow
                  label="Model"
                  value={`${newInv?.model?.brand ?? ""} ${
                    newInv?.model?.type ?? ""
                  }`}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </ReactModal>
  );
};

export default ChangeSerialModal;
